import { CareSitesApi } from "@/api/careSites/CareSitesApi";
import { queryKeys } from "@/api/queryKeys";
import { useAuth } from "@/auth/AuthContext";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export function useMyCareSites() {
  const { user } = useAuth();
  const q = useQuery({
    queryKey: queryKeys.careSites.my,
    queryFn: () => CareSitesApi.listMySites(),
    enabled: !!user?.id,
    staleTime: 15_000,
  });
  return {
    sites: q.data ?? [],
    loading: q.isLoading,
    error: q.error,
    refetch: q.refetch,
  };
}

export function useCareSiteDetail(siteId: string | undefined) {
  const { user } = useAuth();
  const enabled = !!user?.id && !!siteId;

  const siteQ = useQuery({
    queryKey: queryKeys.careSites.detail(siteId ?? ""),
    queryFn: () => CareSitesApi.getSite(siteId!),
    enabled,
  });

  const nodesQ = useQuery({
    queryKey: queryKeys.careSites.nodes(siteId ?? ""),
    queryFn: () => CareSitesApi.listPlaceNodes(siteId!),
    enabled,
  });

  const accessQ = useQuery({
    queryKey: queryKeys.careSites.access(siteId ?? ""),
    queryFn: () => CareSitesApi.listAccess(siteId!),
    enabled,
  });

  const manageQ = useQuery({
    queryKey: queryKeys.careSites.canManage(siteId ?? ""),
    queryFn: () => CareSitesApi.canManageSite(siteId!),
    enabled,
  });

  return {
    site: siteQ.data ?? null,
    nodes: nodesQ.data ?? [],
    access: accessQ.data ?? [],
    canManage: manageQ.data ?? false,
    loading: siteQ.isLoading || nodesQ.isLoading,
    error: siteQ.error || nodesQ.error || accessQ.error,
    refetch: async () => {
      await Promise.all([
        siteQ.refetch(),
        nodesQ.refetch(),
        accessQ.refetch(),
        manageQ.refetch(),
      ]);
    },
  };
}

export function useCreatePrivateCareSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: CareSitesApi.createPrivateSite,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.careSites.my });
    },
  });
}

export function useCreatePlaceNode(siteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: CareSitesApi.createPlaceNode,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.careSites.nodes(siteId) });
    },
  });
}
